import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ui.css';

// Kanonisches Modal (Frontend Phase 2, #32 / F5).
// Ersetzt schrittweise die 31 handgeschriebenen .modal-overlay-Kopien:
// Portal an document.body, ESC + optionaler Overlay-Klick zum Schließen,
// Fokus-Management (Fokus rein beim Öffnen, zurück beim Schließen, Tab-Trap),
// role="dialog"/aria-modal. Styles nur über die :root-Tokens (ui.css).
//
// Props:
//   open           – Modal sichtbar? (false → rendert nichts)
//   onClose        – Pflicht: Schließen-Callback (ESC, X, Overlay)
//   title          – String oder Node für die Kopfzeile
//   icon           – optionales Icon-Node links vom Titel
//   footer         – optionales Node für die Aktionszeile (Buttons)
//   maxWidth       – CSS-Breite des Dialogs (Default '600px')
//   closeOnOverlay – Klick auf den Hintergrund schließt (Default true)
//   closeDisabled  – Schließen sperren, z. B. während eines Speicherns
//   closeLabel     – aria-label des X-Buttons (Default 'Schließen')
//   className      – Zusatzklasse auf dem Dialog
//   ariaLabel      – Dialog-Name für Screenreader, wenn `title` ein Node ist (String-Titel
//                    wird automatisch verwendet)
//
// Modal-Stack (Kehrwoche A, #235 / #32): alle offenen ui-Modals registrieren sich
// modulweit in Öffnungsreihenfolge. Nur das OBERSTE reagiert auf ESC und fängt Tab.
// Vorher lief der ESC-Handler in der Capture-Phase mit stopPropagation — zwei offene
// Modals schlossen gleichzeitig, und fremde ESC-Handler (z. B. ui/Combobox: nur das
// Dropdown schließen) kamen nie zum Zug, ESC im offenen Dropdown schloss den ganzen
// Dialog. Jetzt Bubble-Phase ohne stopPropagation: wer ESC selbst verarbeitet
// (Combobox bei offenem Dropdown), stoppt die Weitergabe — sonst schließt das oberste Modal.
const modalStack = [];
const registerModal = (token) => { modalStack.push(token); };
const unregisterModal = (token) => {
  const idx = modalStack.indexOf(token);
  if (idx >= 0) modalStack.splice(idx, 1);
};
const isTopModal = (token) => modalStack.length > 0 && modalStack[modalStack.length - 1] === token;

const Modal = ({
  open,
  onClose,
  title,
  icon = null,
  footer = null,
  maxWidth = '600px',
  closeOnOverlay = true,
  closeDisabled = false,
  closeLabel = 'Schließen',
  className = '',
  ariaLabel,
  children
}) => {
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const stackTokenRef = useRef(null);
  if (stackTokenRef.current === null) stackTokenRef.current = {};

  const requestClose = useCallback(() => {
    if (!closeDisabled && typeof onClose === 'function') onClose();
  }, [closeDisabled, onClose]);

  // Im Modal-Stack registrieren, solange offen
  useEffect(() => {
    if (!open) return undefined;
    const token = stackTokenRef.current;
    registerModal(token);
    return () => unregisterModal(token);
  }, [open]);

  // ESC schließt (nur das oberste Modal); Tab bleibt im Dialog (einfacher Fokus-Trap)
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (e) => {
      if (!isTopModal(stackTokenRef.current)) return;
      if (e.key === 'Escape') {
        if (e.defaultPrevented) return;
        requestClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    // Bubble-Phase: Elemente im Dialog (Combobox-Dropdown u. a.) dürfen ESC zuerst verarbeiten
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, requestClose]);

  // Fokus beim Öffnen in den Dialog, beim Schließen zurück zum Auslöser
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement;
      if (dialogRef.current) dialogRef.current.focus();
      return () => {
        const prev = previousFocusRef.current;
        if (prev && typeof prev.focus === 'function') {
          try { prev.focus(); } catch (_) {}
        }
      };
    }
    return undefined;
  }, [open]);

  if (!open) return null;

  const handleOverlayMouseDown = (e) => {
    if (closeOnOverlay && e.target === e.currentTarget) requestClose();
  };

  return createPortal(
    <div className="ui-modal-overlay" onMouseDown={handleOverlayMouseDown}>
      <div
        className={`ui-modal ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : ariaLabel}
        style={{ maxWidth }}
        ref={dialogRef}
        tabIndex={-1}
      >
        {(title || icon) && (
          <div className="ui-modal-header">
            <div className="ui-modal-title">
              {icon}
              {typeof title === 'string' ? <h3>{title}</h3> : title}
            </div>
            <button
              type="button"
              className="ui-modal-close"
              onClick={requestClose}
              aria-label={closeLabel}
              disabled={closeDisabled}
            >
              ×
            </button>
          </div>
        )}
        <div className="ui-modal-body">{children}</div>
        {footer && <div className="ui-modal-actions">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
